/**
 * PaymentDetailsView — explicit, API-coupled rendering of the payment's
 * identifier fields, invoice numbers and reissue details.
 *
 * Each field is mapped by name from the GetPaymentDetail response in the
 * mapper, so what shows here is exactly what we chose to show — no generic,
 * shape-based rendering of arbitrary fields. Pure presentational.
 */
import Box from '@cloudscape-design/components/box';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { CopyableText } from 'src/components/common/CopyableText';
import { KeyValue } from 'src/store/slices/paymentDetail/paymentDetailTypes';

/** Uppercase the first letter of a camelCase label: paymentMethod → PaymentMethod. */
const upperFirst = (label: string): string => (label ? label.charAt(0).toUpperCase() + label.slice(1) : label);

interface PaymentDetailsViewProps {
  identifiers: KeyValue[];
  invoiceIds: string[];
  reissue?: KeyValue[];
}

export const PaymentDetailsView = ({ identifiers, invoiceIds, reissue }: PaymentDetailsViewProps) => (
  <SpaceBetween size="m">
    <KeyValuePairs
      columns={4}
      items={[
        ...identifiers.map(field => ({
          label: upperFirst(field.key),
          value: <CopyableText value={field.value} />,
        })),
        { label: 'InvoiceIds', value: invoiceIds.length > 0 ? invoiceIds.join(', ') : '-' },
      ]}
    />
    {reissue && (
      <div>
        <Box variant="awsui-key-label">Reissue details</Box>
        <KeyValuePairs
          columns={3}
          items={reissue.map(field => ({
            label: upperFirst(field.key),
            value: <CopyableText value={field.value} />,
          }))}
        />
      </div>
    )}
  </SpaceBetween>
);
